/* eslint-disable camelcase */
import {
  Badge,
  BodyText,
  Button,
  Drawer,
  DrawerBody,
  DrawerHeader,
} from '@postscript/components';
import { Message, Subscriber } from 'components/sales/types';
import { getCrossShopId } from 'components/sales/utils/viewedShop';
import { useUser } from 'controllers/contexts/user';
import React, { useCallback } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import styled from 'styled-components';

import { DRAWER_TITLE } from './copy';
import { MessageDetailsBody } from './MessageDetailsBody';
import { MessageDetailsSkeleton } from './MessageDetailsSkeleton';
import { MESSAGE_DETAILS_PRESET_LABELS } from './presets';
import { FORCE_OVERRIDE_PARAM } from './storageKey';
import { useMessageDetails } from './useMessageDetails';
import { useMessageDetailsOverride } from './useMessageDetailsOverride';

const HeaderActions = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: var(--spacing-1);
`;

const StatusPillRow = styled.div`
  display: flex;
  justify-content: flex-start;
  margin-bottom: var(--spacing-2);
`;

// Extra room below the last section so the floating Intercom launcher (lower
// right of the app) doesn't sit on top of Subscriber Details.
const BodyBottomSpacer = styled.div`
  height: var(--spacing-10);
`;

const SuppressedOverrideFooter = styled.div`
  display: flex;
  justify-content: center;
  padding: var(--spacing-3) 0 0;
`;

const SuppressedOverrideButton = styled.button`
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 0;
`;

interface MessageDetailsDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  // The sent_message_id powering this drawer instance. The drawer
  // refetches when this changes — re-targeting between messages doesn't
  // require close/reopen.
  sentMessageId: number | undefined;
  // Provided by the parent that owns the editor modal (PR 6). Non-admin
  // callers receive undefined and lose the "Edit contract" affordance.
  onEditContract?: () => void;
  // Live sent message + subscriber, threaded through to the Overview feedback
  // affordance so it targets the same record as the conversation thread.
  message?: Message;
  subscriber?: Subscriber | null;
}

const presetLabel = (
  preset: keyof typeof MESSAGE_DETAILS_PRESET_LABELS | 'custom' | undefined,
): string => {
  if (!preset) return '';
  if (preset === 'custom') return 'Custom';
  return MESSAGE_DETAILS_PRESET_LABELS[preset];
};

export const MessageDetailsDrawer: React.FC<MessageDetailsDrawerProps> = ({
  isOpen,
  onClose,
  sentMessageId,
  onEditContract,
  message,
  subscriber = null,
}) => {
  const { user } = useUser();
  const isAdmin = user?.is_admin === true;

  // Postscript admins/sales agents view another shop's conversation, so the
  // details fetch must target the viewed shop. Merchants viewing their own
  // shop omit the param and keep the JWT fallback.
  const detailsShopId = getCrossShopId(subscriber?.shop_id, user?.shop_id);

  const { clearOverride } = useMessageDetailsOverride();
  const { status, payload, override, hasSuppressedOverride, retry } =
    useMessageDetails(sentMessageId, detailsShopId);

  const history = useHistory();
  const location = useLocation();

  const handleForceOverride = useCallback(() => {
    const params = new URLSearchParams(location.search);
    params.set(FORCE_OVERRIDE_PARAM, '1');
    history.replace({
      pathname: location.pathname,
      search: params.toString(),
    });
  }, [history, location.pathname, location.search]);

  const showResetButton =
    isAdmin && (status === 'override' || status === 'override-forced');
  const showEditButton = isAdmin && onEditContract;
  const showEmptyStateEditorEntry = isAdmin ? onEditContract : undefined;

  const statusPill = (() => {
    if (status === 'override') {
      return (
        <Badge variant="warning" size="small">
          Override active · {presetLabel(override?.preset)}
        </Badge>
      );
    }
    if (status === 'override-forced') {
      return (
        <Badge variant="warning" size="small">
          Override forced via URL · {presetLabel(override?.preset)}
        </Badge>
      );
    }
    return null;
  })();

  const body = (() => {
    if (status === 'loading') return <MessageDetailsSkeleton />;
    // 'empty' and 'error' still render the full body layout — the message
    // bubble, feedback controls, and subscriber identity all come from live
    // props, so only the Message Inputs card collapses to a placeholder.
    if (status === 'empty' || status === 'error') {
      return (
        <MessageDetailsBody
          payload={null}
          message={message}
          subscriber={subscriber}
          detailsUnavailable={status}
          onRetry={retry}
          onOpenEditor={showEmptyStateEditorEntry}
        />
      );
    }
    if (payload)
      return (
        <MessageDetailsBody
          payload={payload}
          message={message}
          subscriber={subscriber}
        />
      );
    return null;
  })();

  return (
    <Drawer
      isOpen={isOpen}
      onCancel={onClose}
      dismissOnBackdropClick
      visibleBackdrop
      lockBackgroundScroll
    >
      <DrawerHeader onCancel={onClose}>{DRAWER_TITLE}</DrawerHeader>
      <DrawerBody>
        {(showResetButton || showEditButton) && (
          <HeaderActions>
            {showResetButton && (
              <Button variant="text" size="small" onClick={clearOverride}>
                Reset to API
              </Button>
            )}
            {showEditButton && (
              <Button variant="text" size="small" onClick={onEditContract}>
                Edit contract
              </Button>
            )}
          </HeaderActions>
        )}
        {statusPill && <StatusPillRow>{statusPill}</StatusPillRow>}
        {body}
        {status === 'live' && hasSuppressedOverride && isAdmin && (
          <SuppressedOverrideFooter>
            <SuppressedOverrideButton
              type="button"
              onClick={handleForceOverride}
            >
              <BodyText as="span" size="small" color="var(--link-color)">
                Saved scenario: {presetLabel(override?.preset)} · Force override
                →
              </BodyText>
            </SuppressedOverrideButton>
          </SuppressedOverrideFooter>
        )}
        <BodyBottomSpacer aria-hidden="true" />
      </DrawerBody>
    </Drawer>
  );
};
